import React, { useState } from "react";
import Box from "@mui/material/Box";
import Collapse from "@mui/material/Collapse";
import Grid from "@mui/material/Grid2";
import Typography from "@mui/material/Typography";
import Accordion from "@mui/material/Accordion";
import AccordionSummary from "@mui/material/AccordionSummary";
import AccordionDetails from "@mui/material/AccordionDetails";
import Tooltip from "@mui/material/Tooltip";
import IconButton from "@mui/material/IconButton";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import InfoIcon from "@mui/icons-material/Info";
import CurrencyField from "./input-fields/CurrencyField";
import SliderField from "./input-fields/SliderField";
import IntegerField from "./input-fields/IntegerField";
import RadioField from "./input-fields/RadioField";
import PercentageField from "./input-fields/PercentageField";
import QuestionGroup from "./QuestionGroup";
import AdvancedSettings from "./AdvancedSettings";
import ConditionalQuestionGroup from "./ConditionalQuestionGroup";

const Question = ({ question, tooltip, answers, errors, handleChange, isVisible }) => {
  const [showTooltip, setShowTooltip] = useState(false);

  const renderField = (field) => {
    switch (field.field_type) {
      case "currency":
        return (
          <CurrencyField
            label={field.label}
            value={answers[field.id]}
            error={!!errors[field.id]}
            onChange={(v) => handleChange(field.id, v)}
            endAdornment={field.endAdornment || " "}
          />
        );
      case "slider":
        return (
          <Box >
            <SliderField
              value={answers[field.id]}
              onChange={(v) => handleChange(field.id, v)}
              min={field.min}
              max={field.max}
              step={field.step}
              marks={
                Array.isArray(field.marks)
                  ? field.marks
                  : Object.keys(field.marks).map(k => ({ value: Number(k), label: field.marks[k] }))
              }
              valueSuffix={field.valueSuffix}
            />
          </Box>
        );
      case "percentage":
        return (
          <PercentageField
            label={field.label}
            value={answers[field.id]}
            error={!!errors[field.id]}
            onChange={(v) => handleChange(field.id, v)}
          />
        );
      case "number":
        return (
          <IntegerField
            label={field.label}
            value={answers[field.id]}
            error={!!errors[field.id]}
            onChange={(v) => handleChange(field.id, v)}
          />
        );
      case "radio":
        return (
          <RadioField
            config={field}
            value={answers[field.id]}
            onChange={(v) => handleChange(field.id, v)}
          />
        );
      case "collapsible_group":
        return (
          <Accordion>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography>{field.question}</Typography>
              {field.tooltip && (
                <Tooltip title={field.tooltip}>
                  <IconButton edge="end">
                    <InfoIcon />
                  </IconButton>
                </Tooltip>
              )}
            </AccordionSummary>
            <AccordionDetails>
              <QuestionGroup 
                question={field} 
                isVisible={isVisible} 
                renderField={renderField} 
              />
            </AccordionDetails>
          </Accordion>
        );
      case "conditional_group":
        return (
          <ConditionalQuestionGroup 
            question={field} 
            isVisible={isVisible} 
            renderField={renderField} 
          />
        );
      default:
        return null;
    }
  };

  const renderQuestionContent = () => {
    if (question.id === "advanced_settings") {
      return (
        <AdvancedSettings 
          question={question}
          answers={answers}
          errors={errors}
          isVisible={isVisible}
          handleChange={handleChange}
          renderField={renderField}
        />
      );
    }
    if (question.field_type === "group") {
      return (
        <QuestionGroup
          question={question}
          isVisible={isVisible}
          renderField={renderField}
        />
      );
    }
    if (question.field_type === "collapsible_group") {
      return renderField(question);
    }
    return renderField(question);
  };

  return (
    <div>
      {question.id !== "advanced_settings" && (
        <Box
          component="div"
          sx={tooltip ? {
            display: "flex",
            alignItems: "center",
            cursor: "pointer",
            "&:hover .tooltipIcon": { color: "primary.main", opacity: 1 }
          } : { display: "flex", alignItems: "center"}}
          onClick={tooltip ? () => setShowTooltip(!showTooltip) : undefined}
        >
          <span style={{ fontSize: "16px", fontWeight: "bold" }}>{typeof question.question === "string" ? question.question : "Question"}</span>
          {tooltip && (
            <Box sx={{ ml: 1 }}>
              <Box component="span" className="tooltipIcon" sx={{ color: "#19284F", opacity: 0.5, fontSize: "14px" }}>
                (?)
              </Box>
            </Box>
          )}
        </Box>
      )}
      {tooltip && question.id !== "advanced_settings" && (
        <Collapse unmountOnExit in={showTooltip} timeout="auto">
          <Box
            sx={{
              border: '1px solid rgba(0, 0, 0, 0.05)',
              boxShadow: '0px 2px 4px -1px rgba(0, 0, 0, 0.05)',
              backgroundColor: '#F9F9FB',
              mb: 1,
              mt: 1,
              p: 2,
              borderRadius: 1,
              width: "100%"
            }}
          >
            {tooltip.trim().startsWith("<table") ? (
              <div dangerouslySetInnerHTML={{ __html: tooltip }} />
            ) : (
              <p style={{ margin: 0, fontSize: "0.875rem" }}>{tooltip}</p>
            )}
          </Box>
        </Collapse>
      )}
      <Box mt={1} style={{ width: "100%" }}>
        {renderQuestionContent()}
      </Box>
    </div>
  );
};

export default Question;